/**
 * 跨代理店优惠码查询 Panel —— /dealers Tabs 之一。
 * 首屏自动加载最近 20 条；输入 q 后按 code / 限制邮箱 / 实际使用者邮箱三路部分匹配。
 */
import { Anchor, Badge, Box, Button, Card, Group, Table, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { lookupPromotionCodes, type PromoLookupRow } from '@mygpt/app/apis/services/dealer';

import { promoStatusBadge } from './promoStatus';

export const LookupPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<PromoLookupRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchRecent = async () => {
    setLoading(true);
    try {
      const r = await lookupPromotionCodes({ limit: 20 });
      setResults(r.data);
      setHasSearched(false);
    } catch (e: any) {
      notifications.show({ color: 'red', message: e?.response?.data?.detail || String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecent();
  }, []);

  const run = async () => {
    const term = q.trim();
    if (!term) {
      // 清空回到「最近 20 条」视图
      await fetchRecent();
      return;
    }
    setLoading(true);
    try {
      const r = await lookupPromotionCodes({ q: term, limit: 30 });
      setResults(r.data);
      setHasSearched(true);
    } catch (e: any) {
      notifications.show({ color: 'red', message: e?.response?.data?.detail || String(e) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <Title order={4}>{hasSearched ? t('dealer.lookup_title') : t('dealer.lookup_recent_title')}</Title>
        <Group gap="xs">
          <TextInput
            placeholder={t('dealer.lookup_input_placeholder')}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') run();
            }}
            leftSection={<IconSearch size={14} />}
            w={320}
          />
          <Button onClick={run} loading={loading}>
            {t('dealer.query')}
          </Button>
          {hasSearched && (
            <Button
              variant="subtle"
              onClick={() => {
                setQ('');
                fetchRecent();
              }}
            >
              {t('dealer.lookup_reset')}
            </Button>
          )}
        </Group>
      </Group>

      {results.length === 0 ? (
        <Text c="dimmed" ta="center" py="md">
          {loading ? '' : hasSearched ? t('dealer.lookup_empty') : t('dealer.no_dealers')}
        </Text>
      ) : (
        <Box>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>{t('dealer.list_title')}</Table.Th>
                <Table.Th>{t('promotion_code.customer_label')}</Table.Th>
                <Table.Th>{t('promotion_code.redeemed_by_label')}</Table.Th>
                <Table.Th>{t('promotion_code.redeemed_label')}</Table.Th>
                <Table.Th>{t('dealer.status')}</Table.Th>
                <Table.Th>{t('promotion_code.expires_label')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.map((r) => {
                const badge = promoStatusBadge(r, t);
                return (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Text ff="monospace" fw={600}>
                        {r.code}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Anchor onClick={() => navigate(`/dealers/${r.dealer.id}`)}>
                        {r.dealer.code} / {r.dealer.name}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>{r.customer_email || t('promotion_code.customer_any')}</Table.Td>
                    <Table.Td>{r.redeemed_by.length > 0 ? r.redeemed_by.join(', ') : '—'}</Table.Td>
                    <Table.Td>
                      {r.redeemed_count} / {r.max_redemptions}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={badge.color} variant="light">
                        {badge.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {r.expires_at ? dayjs(r.expires_at).format('YYYY-MM-DD') : t('promotion_code.no_expiry')}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Card>
  );
};
